/**
 * Pure waveform helpers — peak extraction + Canvas rendering.
 *
 * Kept framework-agnostic so the same code paints:
 *   • Live recording bars (FFT samples from an AudioContext analyser)
 *   • Static playback bars (pre-computed peaks from a decoded blob)
 *
 * Performance: rendering is O(barCount), called at 60fps during
 * recording and once per playback frame for scrub. We avoid allocs
 * inside the hot path (caller passes pre-sized arrays).
 */

/**
 * Extract `barCount` peak amplitudes (0..1) from a decoded PCM buffer.
 * Used at upload time so the FE doesn't have to decode the blob each
 * time it renders the playback waveform.
 */
export function extractPeaks(samples: Float32Array, barCount: number): number[] {
  const peaks: number[] = new Array(barCount).fill(0);
  if (samples.length === 0 || barCount === 0) return peaks;
  const samplesPerBar = Math.max(1, Math.floor(samples.length / barCount));
  for (let i = 0; i < barCount; i++) {
    const start = i * samplesPerBar;
    const end = Math.min(samples.length, start + samplesPerBar);
    let peak = 0;
    for (let j = start; j < end; j++) {
      const abs = Math.abs(samples[j]);
      if (abs > peak) peak = abs;
    }
    peaks[i] = Math.min(1, peak);
  }
  return peaks;
}

/**
 * Decode a recorded audio Blob into a mono Float32Array via the
 * AudioContext used by the browser. Returns the channel-0 samples
 * (mono assumption — voice messages are mono on capture).
 */
export async function decodeBlobToSamples(
  blob: Blob,
  audioContext: AudioContext,
): Promise<Float32Array> {
  const arrayBuffer = await blob.arrayBuffer();
  const decoded = await audioContext.decodeAudioData(arrayBuffer);
  // Mono — voice recorder forces single channel; if a stereo blob slips
  // in (paranoia) we still take channel 0.
  return decoded.getChannelData(0);
}

export type WaveformPaintOptions = {
  /** CSS color for active (played / recorded) bars. */
  activeColor: string;
  /** CSS color for inactive (unplayed) bars. */
  inactiveColor: string;
  /** Progress 0..1 — bars left of this are "active". */
  progress?: number;
  /** Bar width in CSS pixels. Default 2. */
  barWidth?: number;
  /** Gap between bars in CSS pixels. Default 2. */
  barGap?: number;
  /** Minimum bar height in CSS pixels — visible silence baseline. */
  minHeight?: number;
};

/**
 * Paint `peaks` into `canvas`. Honors devicePixelRatio so the result
 * stays crisp on retina screens (we resize the backing buffer once
 * per draw call — cheap since voice waveforms are tiny).
 */
export function paintWaveform(
  canvas: HTMLCanvasElement,
  peaks: ArrayLike<number>,
  opts: WaveformPaintOptions,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const cssWidth = canvas.clientWidth || canvas.width;
  const cssHeight = canvas.clientHeight || canvas.height;
  // Resize backing store only when needed — avoids GPU thrash on
  // every frame during recording.
  const desiredW = Math.floor(cssWidth * dpr);
  const desiredH = Math.floor(cssHeight * dpr);
  if (canvas.width !== desiredW) canvas.width = desiredW;
  if (canvas.height !== desiredH) canvas.height = desiredH;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssWidth, cssHeight);

  const barWidth = opts.barWidth ?? 2;
  const barGap = opts.barGap ?? 2;
  const minHeight = opts.minHeight ?? 2;
  const slot = barWidth + barGap;
  const visibleBars = Math.max(1, Math.floor(cssWidth / slot));
  const centerY = cssHeight / 2;
  const progress = Math.max(0, Math.min(1, opts.progress ?? 1));
  const activeBarIndex = Math.floor(progress * visibleBars);

  // Resample peaks to visibleBars so the waveform fills the canvas
  // regardless of how many peaks we have.
  for (let i = 0; i < visibleBars; i++) {
    const peakIdx = peaks.length === 0 ? 0 : Math.floor((i / visibleBars) * peaks.length);
    const peakRaw = peaks[peakIdx] ?? 0;
    const barH = Math.max(minHeight, peakRaw * cssHeight * 0.9);
    const x = i * slot + barGap / 2;
    ctx.fillStyle = i < activeBarIndex ? opts.activeColor : opts.inactiveColor;
    // Rounded-rect bars (Orange Voice Memos style). Falls back to plain
    // fillRect on browsers without roundRect (Safari <16).
    if (typeof ctx.roundRect === "function") {
      ctx.beginPath();
      ctx.roundRect(x, centerY - barH / 2, barWidth, barH, barWidth / 2);
      ctx.fill();
    } else {
      ctx.fillRect(x, centerY - barH / 2, barWidth, barH);
    }
  }
}

/**
 * Convert seconds to "0:42" / "1:23" display string. Caps at hours
 * for the rare edge case a coach sends a very long voice memo.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
